import { ReactElement, useEffect } from 'react'

import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import { ErrorPage } from 'components/ErrorBoundary'
import { LoadingIndicator } from 'components/LoadingIndicator'
import { loadAuth } from 'features/Auth/authService'
import { ReconnectingPage } from 'features/Auth/components/ReconnectingPage/ReconnectingPage'
import { useIsReconnecting } from 'features/Auth/reconnectStatus'
import useAppOfflineStatus from 'helpers/appOfflineStatus'
import { useAuthDomainValue } from 'hooks'
import { BASE_ROUTES } from 'routing/BaseRoutes'
import { DowntimePage } from 'views/DowntimePage'
import { MaintenancePage } from 'views/MaintenancePage'

// Basenames should start with a slash and not end with one.
function formatBasename(path: string): string {
    let formattedPath = path
    if (formattedPath.endsWith('/')) {
        formattedPath = formattedPath.slice(0, -1)
    }
    return formattedPath || '/'
}

const router = createBrowserRouter(BASE_ROUTES, {
    basename: formatBasename(ASSET_PATH),
})

function App(): ReactElement {
    /*
    If an _app_offline.json file is present in the root of the site, we will
    force the app to display the maintenance screen. This is useful for forcing
    the app into an offline state for maintenance purposes when the database may
    not be accessible and we can not enable the maintenance page through admin settings.
*/
    const { isOffline, customOfflineMessage: offlineMessage } =
        useAppOfflineStatus()

    const signedIn = useAuthDomainValue('signedIn')

    useEffect(() => {
        // loadAuth rethrows after dispatching loadAuthFailure (which drives the
        // error UI); swallow the rejection here so it isn't an unhandled promise.
        void loadAuth().catch(() => {})
    }, [signedIn])

    const isLoadingAuth = useAuthDomainValue('isLoadingAuth')
    const loadAuthError = useAuthDomainValue('loadAuthError')

    const displayMaintenancePageSetting = useAuthDomainValue(
        'displayMaintenancePage'
    )
    const displayDowntimePageSetting = useAuthDomainValue('displayDowntimePage')
    const isReconnecting = useIsReconnecting()

    // The offline file is checked first: loadAuth keeps retrying while Cerebro
    // is down, so the loading and reconnecting states could otherwise hide it.
    if (isOffline) {
        return <MaintenancePage message={offlineMessage} />
    }

    // Ahead of the loading state, which stays on while the bootstrap retries.
    if (isReconnecting) {
        return <ReconnectingPage />
    }

    if (isLoadingAuth) {
        return (
            <LoadingIndicator
                size="small"
                style={{ position: 'relative', minHeight: '300px' }}
            />
        )
    }

    if (displayMaintenancePageSetting) {
        return <MaintenancePage message={offlineMessage} />
    }

    if (displayDowntimePageSetting) {
        return <DowntimePage />
    }

    if (loadAuthError) {
        return <ErrorPage />
    }

    return <RouterProvider router={router} />
}

export default withLDConsumer()(App)
